import { calculateMintAmount, } from "./calculateMintAmount";
import { calculateBurnAmount, } from "./calculateBurnAmount";
// import { calculateSwapAmount, } from "./calculateSwapAmount";
import { isNativeToken, } from "./isNativeToken";
import { latestPriceEth, bracketPrice, bracketRate, } from "@cachables";
import type { SwapMode, } from "@types";
import type {
	AmountString,
	TokenIdentifier,
	TokenDefinition,
} from "@bracket/types";
import { getTokenDefinition } from "@bracket/chainography";



export declare type GetQuoterProps = {
	tokenIn: TokenIdentifier;
	tokenOut: TokenIdentifier;
	amount: AmountString;
};

export declare type GetQuoteProps = GetQuoterProps & {
	mode: SwapMode;
}

export declare type GetQuoteResult = {
	token: TokenDefinition;
	amount: bigint;
	valid: boolean;
} & ({
	native: true;
	calldata: string;
	priceImpact: number;
	gas: number;
} | {
	native: false;
})



/**
 * Gets a quote for swapping or minting tokens.
 * For native tokens (ETH/WETH), calculates swap amount.
 * For other tokens, calculates mint amount.
 * 
 * @param {Object} props - Quote parameters
 * @param {SwapMode} props.mode - Swap mode
 * @param {TokenIdentifier} props.token - Token identifier to get quote for
 * @param {AmountString} props.amount - Amount to get quote for
 * @returns {Promise<GetQuoteResult>} Quote result containing token, amount and calldata
 */
export const getQuote = async ({
	mode,
	tokenIn,
	tokenOut,
	amount,
}: GetQuoteProps ): Promise<GetQuoteResult> => {

	if(Number(amount) <= 0) {;

		return {
			token: getTokenDefinition(tokenIn),
			amount: BigInt(0),
			native: isNativeToken(tokenIn),
			calldata: "",
			priceImpact: 0,
			gas: 0,
			valid: true,
		}

	}

	switch(mode) {

		case "BUY": {

			const result = await calculateMintAmount({ amount, tokenIn, tokenOut, });
			let valid = true;

			if(isNativeToken(tokenIn)) {
				const inDollars = Number(amount) * (await latestPriceEth());
				const outDollars = (Number(result.amount)/1e18) * (await bracketPrice());
				valid = outDollars > (inDollars * 0.9995);
			}

			return Object.assign(result, { valid, });
		}

		case "REDEEM": {

			const result = await calculateBurnAmount({ amount, tokenIn, tokenOut, });
			return Object.assign(result, { valid: true, });
		}
		default:
			throw new Error(`Invalid mode: ${mode}`);

	}

}
